import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiFetch, ApiError } from '../api/client.ts';
import { queryDefaults } from '../api/queries.ts';
import { useSession } from '../auth/session.tsx';
import { LoadFailure } from '../components/LoadFailure.tsx';

interface Session {
  session_id: string;
  staff_name: string;
  email: string;
  started_at: string;
  last_seen_at: string;
  requests: number;
  changes: number;
  client_ip: string | null;
  first_request: string;
}

interface Entry {
  occurred_at: string;
  action: string;
  entity_type: string;
  client_ref: string | null;
  what: string | null;
}

/** An instant, shown in the viewer's zone: a timestamptz genuinely is one. */
function when(ts: string): string {
  return new Date(ts).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/**
 * Break-glass use (062): every session of the emergency account, and what it
 * did, for tier 1 and the Director of Compliance.
 *
 * Reading this is itself recorded in the audit log, as reading the audit log
 * always is. Its home is the admin section's Audit tab once that exists.
 */
export function BreakGlassUse() {
  const { getToken } = useSession();
  const list = useQuery({
    queryKey: ['break-glass', 'sessions'],
    queryFn: () => apiFetch<{ sessions: Session[]; days: number }>('/break-glass/sessions?days=365', getToken),
    ...queryDefaults,
  });
  const [open, setOpen] = useState<string | null>(null);

  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
        <header className="page-head">
          <h1 className="page-title">Break-glass use</h1>
          <p className="mt-0.5 max-w-[60rem] text-sm text-neutral-500">
            Every session of the emergency account in the last year: when, from where, and everything it
            opened or changed. A session ends after 30 minutes with no activity. Looking at this page is
            recorded.
          </p>
        </header>
        <div className="page-body">
          {list.error && !list.data ? (
            list.error instanceof ApiError && list.error.status === 403 ? (
              <div role="alert" className="p-6">
                <h2 className="text-lg font-bold text-neutral-900">Not open to your roles</h2>
                <p className="mt-2 text-neutral-700">{list.error.message}</p>
                <Link to="/clients"
                      className="mt-5 inline-flex min-h-[48px] items-center rounded-lg bg-neutral-900 px-5 font-semibold text-white">
                  Back to clients
                </Link>
              </div>
            ) : (
              <LoadFailure what="Break-glass use" error={list.error}
                           onRetry={() => void list.refetch()} retrying={list.isFetching} />
            )
          ) : !list.data ? (
            <p className="px-4 py-10 text-center text-neutral-600">Loading…</p>
          ) : list.data.sessions.length === 0 ? (
            <p className="px-4 py-10 text-center text-neutral-600">The break-glass account has not been used in the last year.</p>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {list.data.sessions.map((s) => (
                <li key={s.session_id} className="px-4 py-3 lg:px-5">
                  <button type="button" onClick={() => setOpen(open === s.session_id ? null : s.session_id)}
                          className="block min-h-[44px] w-full text-left">
                    <span className="font-semibold text-neutral-900">{when(s.started_at)}</span>
                    <span className="text-neutral-700"> to {when(s.last_seen_at)} · {s.staff_name}</span>
                    <span className="block text-sm text-neutral-600">
                      {s.requests} {s.requests === 1 ? 'request' : 'requests'}, {s.changes}{' '}
                      {s.changes === 1 ? 'change' : 'changes'}
                      {s.client_ip ? ` · from ${s.client_ip}` : ''} · began with {s.first_request}
                    </span>
                  </button>
                  {open === s.session_id && <Trail id={s.session_id} />}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function Trail({ id }: { id: string }) {
  const { getToken } = useSession();
  const trail = useQuery({
    queryKey: ['break-glass', 'session', id],
    queryFn: () => apiFetch<{ entries: Entry[] }>(`/break-glass/sessions/${id}`, getToken),
    ...queryDefaults,
  });
  if (trail.error) return <p className="mt-2 text-sm text-rose-800">{trail.error.message}</p>;
  if (!trail.data) return <p className="mt-2 text-sm text-neutral-600">Loading…</p>;
  return (
    <ol className="mt-2 space-y-1 border-l-2 border-rose-200 pl-3 text-sm">
      {trail.data.entries.map((e, i) => (
        <li key={i} className="text-neutral-800">
          <span className="text-neutral-500">{when(e.occurred_at)}</span>{' '}
          {e.entity_type === 'break_glass_request' || e.entity_type === 'break_glass_session'
            ? <>{e.action === 'LOGIN' ? 'session began with' : 'request'} <span className="font-mono">{e.what}</span></>
            : <>{e.action.toLowerCase()} {e.entity_type}{e.client_ref ? ` for ${e.client_ref}` : ''}
                {e.what ? `: ${e.what}` : ''}</>}
        </li>
      ))}
    </ol>
  );
}
